/**
 * Auth-aware fetch wrapper. Reads the token from tokenStore on every call,
 * adds the Bearer header, and throws on non-2xx so TanStack Query handles errors.
 */
import { getToken, clearToken } from './tokenStore';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export class ApiError extends Error {
  status: number;
  data: unknown;
  /** Short code the server logged this failure under (see api_errors.ref). */
  ref?: string;
  /** Which call failed, for the error banner. Query string removed. */
  method?: string;
  path?: string;
  constructor(status: number, message: string, data: unknown = null) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  // Native fetch() throws TypeError("Failed to fetch") on network failures
  // (offline, DNS, CORS reject, mixed content). The raw message is unhelpful
  // for end users — normalize it to a clear "couldn't reach the server"
  // ApiError so call sites can either show a friendly message or check
  // .status === 0 to detect a network problem specifically.
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch (err) {
    const netErr = new ApiError(
      0,
      "Couldn't reach the server. Check your internet connection and try again.",
      { cause: err instanceof Error ? err.message : String(err) },
    );
    netErr.method = (init.method ?? 'GET').toUpperCase();
    netErr.path = path.split('?')[0];
    throw netErr;
  }

  if (res.status === 401) {
    // Token expired or invalid. Wipe and let the routing layer redirect to login.
    clearToken();
  }

  const text = await res.text();
  const body = text ? safeJsonParse(text) : null;

  if (!res.ok) {
    /*
     * `detail` is appended to the message, not dropped.
     *
     * Daniel 2026-08-05: sending a contract to title failed and the only thing
     * on screen was "Send failed: Email send failed". The API had already put
     * the real reason on the response, one field over:
     *
     *   return c.json({ error: 'Email send failed', detail: message }, 502);
     *
     * where `message` is the provider's own rejection text. This function read
     * `body.error` and threw the rest away, so the one useful sentence never
     * reached the person who needed it. `data` carried it, but every caller
     * renders `err.message`, so in practice it was invisible.
     *
     * A generic error is worse than no error: it costs a support round trip and
     * teaches people the app cannot tell them what went wrong. Endpoints only
     * set `detail` deliberately, so surfacing it is safe rather than noisy.
     *
     * Not blindly stringified: `detail` is sometimes an object, and
     * "[object Object]" would be its own kind of unhelpful.
     */
    const base =
      (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
        ? body.error
        : `HTTP ${res.status}`);
    let message = base;
    if (body && typeof body === 'object' && 'detail' in body) {
      const d = (body as { detail?: unknown }).detail;
      const detail = typeof d === 'string' ? d.trim() : '';
      // Skip a detail that merely repeats the headline.
      if (detail && !base.includes(detail) && detail !== base) {
        message = `${base}. ${detail}`;
      }
    }
    const apiErr = new ApiError(res.status, message, body);
    apiErr.method = (init.method ?? 'GET').toUpperCase();
    apiErr.path = path.split('?')[0];
    if (body && typeof body === 'object' && 'ref' in body && typeof (body as { ref?: unknown }).ref === 'string') {
      apiErr.ref = (body as { ref: string }).ref;
    }
    throw apiErr;
  }

  return body as T;
}

function safeJsonParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

// ---------------------------------------------------------------------
// Auth endpoint helpers
// ---------------------------------------------------------------------

export interface LoginResponse {
  access_token: string;
  expires_at: number;
  user: AuthUser;
  /** S-1 (Daniel 2026-08-04): present only when the login was completed
   *  with a recovery code: how many unused codes are left, so the UI can
   *  tell the user to re-enroll before they run out. */
  recovery_codes_remaining?: number;
}

/** S-1 (Daniel 2026-08-04): what POST /login returns INSTEAD of a session
 *  when the account has two-factor on. The mfa_token is a 5-minute,
 *  single-purpose credential for /mfa/verify. It is NOT stored in
 *  tokenStore and cannot authenticate any other endpoint. */
export interface MfaChallenge {
  mfa_required: true;
  mfa_token: string;
  /** S-2 (Daniel 2026-08-04): which factors this account actually has, so
   *  the code step can label itself honestly and offer the right controls.
   *  Without it the page would have to assume "authenticator", which for an
   *  SMS-only user means a prompt for an app they never installed and no way
   *  to ask for the text. Optional so an older API (or a cached bundle
   *  talking to a newer one) still works: absent is read as TOTP-only, the
   *  S-1 behavior.
   *
   *  Threading: POST /login returns it → login() types it here →
   *  AuthProvider.signIn hands the whole challenge object back untouched →
   *  Login.tsx keeps it in state beside mfa_token. Exactly the path
   *  mfa_token already takes, because it has the same lifetime: both are
   *  meaningless once the code step is done. */
  mfa_methods?: { totp: boolean; sms: boolean };
}

/** Discriminate a login result: challenge vs completed session. */
export function isMfaChallenge(r: LoginResponse | MfaChallenge): r is MfaChallenge {
  return 'mfa_required' in r && r.mfa_required === true;
}

export type Role =
  | 'owner'
  | 'operations'
  | 'admin'
  /* Build 12 (Daniel 2026-09-14): the coach seat. The API widens a coach's
   * roles with 'owner' on every read and refuses every write, so the web
   * sees an owner who cannot save. `AuthUser.coach` is the flag for the
   * banner. See apps/api/src/middleware/auth.ts. */
  | 'coach'
  | 'agent'
  | 'marketing'
  /* V-3 (Daniel 2026-08-12): sees and edits every deal on the team so the PIS
   * and the party info can be filled in on any listing. No production, no
   * closing history, no other agents' earnings, and no money on a deal she is
   * not on. Not to be confused with transactions.showing_assistant_user_id,
   * which is a PER DEAL assignment that DOES carry money on that one deal.
   * apps/api/src/lib/auth.ts carries the full note above its Role union. */
  | 'showing_assistant'
  | 'partner'
  | 'client';
export type Portal = 'agent' | 'partner' | 'client';

/** Roles allowed to manage the team. Admin is intentionally NOT in this list. */
export const TEAM_MANAGER_ROLES: readonly Role[] = ['owner', 'operations'];

/** Roles allowed to edit phase definitions, task templates, email templates. */
export const TEMPLATE_EDITOR_ROLES: readonly Role[] = ['owner', 'operations'];

export function hasAnyRole(userRoles: Role[] | undefined, allowed: readonly Role[]): boolean {
  return (userRoles ?? []).some((r) => allowed.includes(r));
}

export interface AuthUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  must_change_password: boolean;
  team_id: number;
  roles: Role[];
  primary_role: Role;
  portal: Portal;
  /** Filled from /me; may be null if user hasn't set it. */
  phone?: string | null;
  /** Filled from /me; may be null if user hasn't set it. */
  email_signature?: string | null;
  /** Nav IA (2026-07-27): true when this person is the team's transaction
   *  coordinator, from teams.tc_user_id or an explicit 'tc' briefing focus.
   *  Roles can't tell us — a TC is stored as operations/admin like anyone
   *  else on the ops side, and they need a different rail. */
  is_tc?: boolean;
  /** 'agent' | 'operations' | 'tc' | 'listing_marketing' | 'none' | null */
  briefing_focus?: string | null;
  /** Build 12: true for the coach seat. Read-only; the API enforces it. */
  coach?: boolean;
  /** beta_new_deal_ui (0245, Daniel 2026-08-26): per-user opt-in for the
   *  redesigned deal detail page. Absent on pre-0245 responses; treat
   *  undefined the same as false. */
  beta_new_deal_ui?: boolean;
}

export interface MeResponse extends AuthUser {
  phone: string | null;
  email_signature: string | null;
  last_login_at: number | null;
}

// ---------------------------------------------------------------------
// Self-update — used by Profile section of /team
// ---------------------------------------------------------------------
export function updateMyProfile(body: {
  first_name?: string;
  last_name?: string;
  phone?: string | null;
  email_signature?: string | null;
}): Promise<{ ok: true }> {
  return apiFetch('/api/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function login(
  email: string,
  password: string,
  remember = true,
): Promise<LoginResponse | MfaChallenge> {
  // S-1: an MFA-enrolled account gets { mfa_required, mfa_token } here
  // instead of a session; the login page then collects the 6-digit code
  // and finishes via verifyMfaCode below.
  return apiFetch<LoginResponse | MfaChallenge>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, remember }),
  });
}

// ---------------------------------------------------------------------
// S-1 (Daniel 2026-08-04): TOTP two-factor endpoints.
// ---------------------------------------------------------------------

/** Step two of an MFA login: trade the pending token + a code (6-digit
 *  TOTP or a recovery code) for the normal login response. */
export function verifyMfaCode(mfaToken: string, code: string): Promise<LoginResponse> {
  return apiFetch<LoginResponse>('/api/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({ mfa_token: mfaToken, code }),
  });
}

export interface MfaStatus {
  /** S-1 field, unchanged meaning: the AUTHENTICATOR factor is on. Kept as
   *  the authenticator flag rather than widened to "any factor" so the two
   *  sections of TwoFactorCard can each render their own truth. */
  enabled: boolean;
  enrolled_at: number | null;
  /** S-2: same value as `enabled`, named for what it is. New code reads
   *  this one; `enabled` stays for anything already pointed at it. */
  totp_enabled?: boolean;
  /** S-2: the texted-code factor. */
  sms_enabled?: boolean;
  /** S-2: "(316) ***-**50". The API never returns the full number. */
  sms_phone_masked?: string | null;
  sms_enrolled_at?: number | null;
}

/** Enrollment state for the caller (owner/operations only). */
export function getMfaStatus(): Promise<MfaStatus> {
  return apiFetch('/api/auth/mfa/status');
}

/** Stage a fresh secret. Returns it once, with the otpauth URL for the QR. */
export function setupMfa(): Promise<{ secret: string; otpauth_url: string }> {
  return apiFetch('/api/auth/mfa/setup', { method: 'POST' });
}

/** Confirm the authenticator has the secret. The recovery codes in the
 *  response are shown exactly once; only hashes exist server-side. */
export function enableMfa(code: string): Promise<{ ok: true; recovery_codes: string[] }> {
  return apiFetch('/api/auth/mfa/enable', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/** Turn two-factor off. Requires a live TOTP code or an unused recovery
 *  code; a session alone is deliberately not enough. */
export function disableMfa(code: string): Promise<{ ok: true }> {
  return apiFetch('/api/auth/mfa/disable', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

// ---------------------------------------------------------------------
// S-2 (Daniel 2026-08-04): SMS second factor.
//
// The code always goes to a PERSONAL cell. The server refuses any number
// that is a Quo work line (the caller's, a teammate's, or the team general
// line) because those land in the office shared inbox, and it returns that
// refusal as a plain 400 the form shows inline.
// ---------------------------------------------------------------------

/** Step one of SMS enrollment: text a code to `phone` and prove it lands.
 *  Nothing is saved on the user until confirmSmsMfa succeeds. Omit `phone`
 *  to re-send to the number already on file (used by the turn-off flow,
 *  where the browser only ever sees the masked number). */
export function startSmsMfa(phone?: string): Promise<{ ok: true; sent_to_masked: string }> {
  return apiFetch('/api/auth/mfa/sms/start', {
    method: 'POST',
    body: JSON.stringify(phone ? { phone } : {}),
  });
}

/** Step two: the code came back, so turn the factor on. `recovery_codes` is
 *  non-null only when the user had none left to fall back on (they enrolled
 *  SMS without ever setting up an authenticator, or burned every code); it
 *  is shown exactly once, same as the TOTP path. */
export function confirmSmsMfa(
  phone: string,
  code: string,
): Promise<{ ok: true; sms_phone_masked: string; recovery_codes: string[] | null }> {
  return apiFetch('/api/auth/mfa/sms/confirm', {
    method: 'POST',
    body: JSON.stringify({ phone, code }),
  });
}

/** Turn texted codes off. Needs a live factor: an authenticator code, a
 *  texted code, or an unused recovery code. A session alone is not enough,
 *  for the same reason as disableMfa. */
export function disableSmsMfa(code: string): Promise<{ ok: true }> {
  return apiFetch('/api/auth/mfa/sms/disable', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

/** Mid-login: text a code to the number on file. Authenticated by the
 *  5-minute pending token, exactly like verifyMfaCode, because the caller
 *  has no session yet. The destination is read from the user's row
 *  server-side and can never be supplied by the browser. */
export function sendMfaSmsCode(mfaToken: string): Promise<{ ok: true; sent_to_masked: string }> {
  return apiFetch('/api/auth/mfa/sms/send', {
    method: 'POST',
    body: JSON.stringify({ mfa_token: mfaToken }),
  });
}

export function fetchMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>('/api/auth/me');
}

/** Portals this user can actually sign into (agent / partner / client). */
export function listMyPortals(): Promise<{
  current: string;
  portals: Array<{ team_id: number; portal: string }>;
}> {
  return apiFetch('/api/auth/portals');
}

/** Re-mint the token bound to another portal the user has access to.
 *  Lets an owner who is also set up as a lender view the lender portal. */
export function switchPortal(portal: string, teamId?: number): Promise<LoginResponse> {
  return apiFetch<LoginResponse>('/api/auth/switch-portal', {
    method: 'POST',
    body: JSON.stringify({ portal, ...(teamId ? { team_id: teamId } : {}) }),
  });
}

export function changePassword(currentPassword: string, newPassword: string): Promise<{ ok: true }> {
  return apiFetch('/api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

export function logout(): Promise<{ ok: true }> {
  return apiFetch('/api/auth/logout', { method: 'POST' });
}

/**
 * Request a password reset email. Always succeeds (returns generic message)
 * regardless of whether the email is registered — by design, to not leak which
 * addresses have accounts.
 */
export function requestPasswordReset(email: string): Promise<{ ok: true; message: string }> {
  return apiFetch('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Consume a reset token + set a new password. Token comes from the URL on the
 * reset page; the user types the new password into the form.
 */
export function resetPassword(token: string, newPassword: string): Promise<{ ok: true; message: string }> {
  return apiFetch('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}

// ---------------------------------------------------------------------
// Admin (owner-only)
// ---------------------------------------------------------------------

export type ReminderChannel = 'sms' | 'email' | 'both' | 'none';

export interface AdminUserMembership {
  role: Role;
  portal: Portal;
  show_on_production: boolean;
  /** How this agent gets the morning/afternoon activity nudges. Default 'sms'. */
  reminder_channel: ReminderChannel;
  is_active: boolean;
}

export interface AdminUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  quo_number: string | null;
  email_signature: string | null;
  /** R-3 (Daniel 2026-08-04): "Gets new-lead alert emails" toggle. */
  lead_alerts: boolean;
  last_login_at: number | null;
  must_change_password: boolean;
  locked: boolean;
  memberships: AdminUserMembership[];
  primary_role: Role;
  /** Buyer-ladder tier year (migration 0345). Null = not set yet. */
  tier_year_basis: 'calendar' | 'anniversary' | null;
  /** '05-01' style, or null. */
  anniversary_md: string | null;
}

export interface CreateUserBody {
  email: string;
  first_name: string;
  last_name: string;
  portal: Portal;
  roles: Role[];
  show_on_production: boolean;
}

export interface CreateUserResponse {
  ok: true;
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  portal: Portal;
  roles: Role[];
  show_on_production: boolean;
  temporary_password: string;
  message: string;
}

export function listTeamUsers(): Promise<{ users: AdminUser[] }> {
  return apiFetch('/api/admin/users');
}

export function createTeamUser(body: CreateUserBody): Promise<CreateUserResponse> {
  return apiFetch('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateMemberships(
  userId: number,
  body: {
    portal: Portal; roles: Role[]; show_on_production: boolean; reminder_channel?: ReminderChannel; is_active: boolean;
    tier_year_basis?: 'calendar' | 'anniversary'; anniversary_md?: string | null;
  },
): Promise<{ ok: true }> {
  return apiFetch(`/api/admin/users/${userId}/memberships`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function updateUserProfile(
  userId: number,
  body: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string | null;
    /** M-2 (2026-07-29): agent's Quo business line. */
    quo_number?: string | null;
    email_signature?: string | null;
    /** R-3 (2026-08-04): "Gets new-lead alert emails" toggle. */
    lead_alerts?: boolean;
  },
): Promise<{ ok: true }> {
  return apiFetch(`/api/admin/users/${userId}/profile`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function resetUserPassword(userId: number): Promise<{
  ok: true;
  email: string;
  temporary_password: string;
  message: string;
}> {
  return apiFetch(`/api/admin/users/${userId}/reset-password`, { method: 'POST' });
}

export function deleteTeamUser(userId: number): Promise<{ ok: true }> {
  return apiFetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------
// Team branding — logo + email footer (owner/operations editable)
// ---------------------------------------------------------------------

export interface TeamSettings {
  id: number;
  slug: string;
  name: string;
  logo_url: string | null;
  email_footer_html: string | null;
  /** Custom Claude daily cap in tenths-of-cent. Null = inherit the
   *  app default ($10/day, defined server-side in cost-cap.ts). */
  claude_daily_cap_tenths_cents: number | null;
  /** Brokerage name shown under the team name on public listing pages.
   *  e.g. "Keller Williams Hometown Partners". */
  brokerage_name: string | null;
  /** Supervising broker at the brokerage — the person who signs on
   *  the brokerage's behalf on BRRETA and similar Kansas forms.
   *  Surfaced as {{team_broker_name}} in every fill path. */
  broker_name?: string | null;
  /** Compliance logo for the umbrella brokerage (KWHP).
   *  Rendered alongside the team logo in the public listing footer. */
  brokerage_logo_url: string | null;
  /** Days without activity before a lead/contact is "stale" — drives
   *  the Monday digest email. 0 disables. (Round D 2026-06-04) */
  stale_threshold_days: number;
  /** L-38 (2026-06-27): team-wide default commission rate as a decimal
   *  (0.03 = 3%). Null = use the code default (currently 0.03). The
   *  Pipelines view's Probable/Potential GCI math reads this. */
  default_commission_rate: number | null;
  /** L-38: comma-separated yyyy-mm-dd list of dates the daily huddle
   *  briefing should skip (holidays, team retreats). Null/empty
   *  string = no blackouts. */
  huddle_blackout_dates: string | null;
  /* ─── Smart Plans (0151-0156) ────────────────────────────────────── */
  /** IANA zone. Everything the plan scheduler does resolves against it:
   *  the 8am-8pm window, "day 7", and blackout dates. */
  timezone?: string;
  /** Max automated messages one person receives per rolling week, across
   *  every plan and every automation. 0 disables the cap. */
  plan_touch_cap_per_week?: number;
  /** Sends attempted per 15-minute tick. Stops an enrollment of 400
   *  becoming 400 texts at once. */
  plan_sends_per_tick?: number;
  /** JSON array of yyyy-mm-dd. Nothing sends on these days. */
  plan_blackout_dates?: string | null;
  /** Plans that fire when a deal reaches these states. */
  under_contract_plan_id?: number | null;
  closed_plan_id?: number | null;
  /** Build 7: Smart Plan fired when a buyer account goes hot. */
  hot_buyer_plan_id?: number | null;
  /** R-1 (Rachelle 2026-08-03): JSON array of extra Cc addresses shown
   *  in SendEmailModal's Cc picker (e.g. daniel@bidwichita.com). */
  extra_cc_json?: string | null;

  /* ─── C-2 (Daniel 2026-08-10): the team's commission standard, 0193 ──
   *
   * All five are DECIMALS (0.06 = 6 percent), matching how the server
   * stores them and how lib/commission.ts works.
   *
   * Optional in this type because the server strips them for anybody
   * outside canSeeMoney: /api/team is open to the client and lender
   * portals so they can read /api/team/phases, and TurnKey's commission
   * terms are not their business. Undefined here means "you are not
   * allowed to see this", not "it is unset". The columns are NOT NULL.
   *
   * Note default_total_commission_pct is NOT the same fact as
   * default_commission_rate above. That one is the one-sided pipeline
   * FORECAST rate (0.03 in production); this one is the total commission
   * in the agreement, both sides (0.06). See migration 0193.
   */
  default_total_commission_pct?: number;
  default_agent_split_pct?: number;
  default_showing_partner_pct?: number;
  default_referral_pct?: number;
  default_buyer_premium_pct?: number;
  /** Build 22 (Daniel 2026-09-18): the team's side of a BUYER deal, one side
   *  of the agreement rather than both. Migration 0268. */
  default_buyer_commission_pct?: number;
}

export function getTeamSettings(): Promise<TeamSettings> {
  return apiFetch('/api/team/settings');
}

// ─── L-21: submission notification recipients ────────────────────────
export interface SubmissionRecipientUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}
export interface SubmissionRecipientsResponse {
  team_users: SubmissionRecipientUser[];
  extras: string[];
}
export function getSubmissionRecipients(): Promise<SubmissionRecipientsResponse> {
  return apiFetch('/api/team/submission-recipients');
}
export function updateSubmissionRecipients(extras: string[]): Promise<{ ok: true; extras: string[] }> {
  return apiFetch('/api/team/submission-recipients', {
    method: 'PATCH',
    body: JSON.stringify({ extras }),
  });
}

// ─── Build 13 (Daniel 2026-09-15): deal milestone alerts ─────────────
export type MilestoneKind = 'active' | 'under_contract' | 'closed';
export interface MilestoneAlertUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  role: string;
  /** Owner / operations / admin, or the team's TC or ops manager. */
  auto_included: boolean;
}
export interface MilestoneAlertsResponse {
  kinds: MilestoneKind[];
  user_ids: number[];
  include_roles: boolean;
  include_agent: boolean;
  send_email: boolean;
  team_users: MilestoneAlertUser[];
}
export function getMilestoneAlerts(): Promise<MilestoneAlertsResponse> {
  return apiFetch('/api/team/milestone-alerts');
}
export function updateMilestoneAlerts(
  body: Omit<MilestoneAlertsResponse, 'team_users'>,
): Promise<{ ok: true } & Omit<MilestoneAlertsResponse, 'team_users'>> {
  return apiFetch('/api/team/milestone-alerts', { method: 'PATCH', body: JSON.stringify(body) });
}

// ─── L-29 (Daniel 2026-06-24): anniversary backfill ──────────────────
export interface BackfillAnniversariesResponse {
  ok: true;
  scanned: number;
  updated: number;
  skipped_already_set: number;
  skipped_no_contact: number;
  /** L-32: diagnostic — close_date couldn't be parsed. Usually 0. */
  skipped_bad_date: number;
}
export function backfillAnniversaries(): Promise<BackfillAnniversariesResponse> {
  return apiFetch('/api/admin/backfill-anniversaries', { method: 'POST' });
}

export interface BuyerTierAgent {
  id: number;
  first_name: string;
  last_name: string | null;
  ytd_buyer_volume: number;
  closed_buyer_deals: number;
  current_tier_pct: number;
  next_tier_pct: number | null;
  to_next_tier: number | null;
  /** Which year the volume is counted in (Daniel 2026-10-27). */
  tier_year_basis?: 'calendar' | 'anniversary';
  /** "2026" or "Since May 1, 2026". */
  window_label?: string;
}
export interface BuyerTiersResponse {
  year: number;
  tier_schedule: Array<{ up_to: number | null; agent_pct: number }>;
  agents: BuyerTierAgent[];
}
export function getBuyerTiers(): Promise<BuyerTiersResponse> {
  return apiFetch('/api/team/buyer-tiers');
}

export function updateTeamSettings(body: {
  logo_url?: string | null;
  email_footer_html?: string | null;
  /** Daily Claude budget in DOLLARS. Pass null/0 to inherit the app
   *  default. Server clamps to [$1, $1000]. */
  claude_daily_cap_dollars?: number | null;
  brokerage_name?: string | null;
  /** Supervising broker's name (BRRETA, Wire Fraud, Mediation). Null clears. */
  broker_name?: string | null;
  /** Stale-digest threshold in days. 0 disables. 1-365 valid. */
  stale_threshold_days?: number;
  /** L-38: default commission rate as percent (0-100). Null = inherit
   *  the code default. */
  default_commission_pct?: number | null;
  /** L-38: yyyy-mm-dd dates to skip the daily huddle email. Pass an
   *  array; server validates each + dedupes + sorts. Empty array
   *  clears the setting. */
  huddle_blackout_dates?: string[] | null;
  /** L-62a (Daniel 2026-06-29): team office street address surfaced
   *  as {{team_office_address}} in transactional templates. */
  office_address?: string | null;
  /** L-62a: earnest-money payment URL surfaced as {{team_payment_link}}. */
  payment_link?: string | null;
  /** Phase 3 (2026-07-05): Google review URL surfaced as
   *  {{google_review_url}} in the day-7 post-close review ask. */
  google_review_url?: string | null;
  /** L-82 (Daniel 2026-07-04): Transaction Coordinator + Ops Manager
   *  user ids. Resolved server-side into {{tc_name}} etc. merge
   *  fields. Pass null to clear. */
  tc_user_id?: number | null;
  ops_manager_user_id?: number | null;
  /** R-1 (Rachelle 2026-08-03): extra Cc addresses for the Cc picker.
   *  Array of emails; empty array clears. */
  extra_cc_emails?: string[] | null;
  /* ─── C-2 (Daniel 2026-08-10): the team's commission standard ────────
   *
   * PERCENTS on the way in (6, 60, 10, 3, 10), decimals in the database.
   * Same convention default_commission_pct has used since L-38, and the
   * reason the read type above is decimals while this one is percents.
   *
   * Not nullable: there is always a team standard. Changing one affects
   * deals created FROM NOW ON and cannot move an existing deal, open or
   * closed, because each deal carries its own frozen copy. Owner and
   * operations only, enforced server-side.
   */
  default_total_commission_pct?: number;
  default_agent_split_pct?: number;
  default_showing_partner_pct?: number;
  default_referral_pct?: number;
  default_buyer_premium_pct?: number;
  /** Build 22 (Daniel 2026-09-18): the buyer side. Migration 0268. */
  default_buyer_commission_pct?: number;
  /* ─── Smart Plans (0151-0156) ────────────────────────────────────── */
  timezone?: string;
  plan_touch_cap_per_week?: number;
  plan_sends_per_tick?: number;
  plan_blackout_dates?: string[] | null;
  /** Must be an active, non-library plan on this team. The server
   *  rejects an inactive one rather than letting a trigger look
   *  configured while doing nothing. */
  under_contract_plan_id?: number | null;
  closed_plan_id?: number | null;
  /** Build 7: Smart Plan fired when a buyer account goes hot. */
  hot_buyer_plan_id?: number | null;
}): Promise<{ ok: true }> {
  return apiFetch('/api/team/settings', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** Today's Claude spend + the effective cap for the caller's team. */
export interface ClaudeUsageToday {
  spent_tenths_cents: number;
  cap_tenths_cents: number;
  percent: number;
  resets_at: string;
}
export function getClaudeUsage(): Promise<ClaudeUsageToday> {
  return apiFetch('/api/team/claude-usage');
}

/** Admin usage dashboard data — last N days broken down by day/feature.
 *  Owner/operations/admin only. */
export interface AdminUsageResp {
  days: number;
  today_tenths_cents: number;
  cap_tenths_cents: number;
  by_day_feature: Array<{ day: string; feature: string; calls: number; total_tenths_cents: number }>;
  by_day: Array<{ day: string; total_tenths_cents: number }>;
  by_feature: Array<{ feature: string; total_tenths_cents: number; calls: number }>;
  by_user: Array<{ user_id: number | null; name: string; calls: number; total_tenths_cents: number }>;
}
export interface AdminErrorRow {
  id: number; ref: string | null; method: string; path: string; message: string;
  status: number | null; occurred_at: number; user_id: number | null;
  first_name: string | null; last_name: string | null;
}
export interface AdminErrorsResp {
  days: number;
  errors: AdminErrorRow[];
  top: { method: string; path: string; message: string; n: number; last_at: number }[];
}
export function getAdminErrors(days: number, q: string): Promise<AdminErrorsResp> {
  return apiFetch(`/api/admin/errors?days=${days}&q=${encodeURIComponent(q)}`);
}

export function getAdminClaudeUsage(days = 30): Promise<AdminUsageResp> {
  return apiFetch(`/api/admin/claude-usage?days=${days}`);
}

// ─── Agent public profiles + reviews ────────────────────────────────
// Listing Leads-style branded review pages. Each team member can have
// a /public/agents/:slug page once slug + bio are set. Reviews are
// manually curated by ops/owner/admin.

export interface AgentPublicProfile {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  public_slug: string | null;
  /** URL of the agent's headshot. Paste-in for now (Cloudinary, Imgur,
   *  agent's own site). We can add proper R2 file upload later. */
  public_avatar_url: string | null;
  bio_short: string | null;
  service_areas: string | null;
  social_instagram: string | null;
  social_facebook: string | null;
  social_linkedin: string | null;
  social_youtube: string | null;
  social_tiktok: string | null;
  social_website: string | null;
  review_count: number;
}

export function listAgentProfiles(): Promise<{ profiles: AgentPublicProfile[] }> {
  return apiFetch('/api/admin/agent-profiles');
}

export function updateAgentProfile(
  userId: number,
  patch: Partial<Omit<AgentPublicProfile, 'user_id' | 'first_name' | 'last_name' | 'email' | 'phone' | 'review_count'>>,
): Promise<{ ok: true }> {
  return apiFetch(`/api/admin/agent-profiles/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export interface AdminReview {
  id: number;
  user_id: number;
  author_name: string;
  rating: number;
  body: string;
  source: string | null;
  source_url: string | null;
  is_published: number; // 0|1
  posted_at: number | null;
  position: number;
  created_at: number;
}

export function listAgentReviews(userId: number): Promise<{ reviews: AdminReview[] }> {
  return apiFetch(`/api/admin/agent-profiles/${userId}/reviews`);
}

export function createAgentReview(
  userId: number,
  body: {
    author_name: string;
    rating: number;
    body: string;
    source?: string;
    source_url?: string;
    posted_at?: number | null;
    is_published?: boolean;
  },
): Promise<{ ok: true; id: number }> {
  return apiFetch(`/api/admin/agent-profiles/${userId}/reviews`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateAgentReview(
  userId: number,
  reviewId: number,
  patch: Partial<{
    author_name: string;
    rating: number;
    body: string;
    source: string | null;
    source_url: string | null;
    posted_at: number | null;
    is_published: boolean;
    position: number;
  }>,
): Promise<{ ok: true }> {
  return apiFetch(`/api/admin/agent-profiles/${userId}/reviews/${reviewId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function deleteAgentReview(userId: number, reviewId: number): Promise<{ ok: true }> {
  return apiFetch(`/api/admin/agent-profiles/${userId}/reviews/${reviewId}`, {
    method: 'DELETE',
  });
}

/** Upload a headshot to R2. Returns the persisted public URL.
 *  Auth-aware multipart so we can't use the typed apiFetch JSON helper. */
export async function uploadAgentAvatar(userId: number, file: File): Promise<{ ok: true; public_avatar_url: string }> {
  const { getToken } = await import('./tokenStore');
  const token = getToken();
  if (!token) throw new Error('Not signed in');
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${apiBase}/api/admin/agent-profiles/${userId}/avatar`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export function deleteAgentAvatar(userId: number): Promise<{ ok: true }> {
  return apiFetch(`/api/admin/agent-profiles/${userId}/avatar`, { method: 'DELETE' });
}

/** Marketing-post approval queue — submitted posts the caller can act on. */
export interface ApprovalQueueItem {
  transaction_id: number;
  transaction_type: 'auction' | 'listing' | 'buyer';
  address: string;
  post_id: string;
  post_title: string;
  post_format: string;
  day_offset: number;
  submitted_at: number | null;
  submitted_by_first: string | null;
  submitted_by_last: string | null;
  stale: boolean;
  /** Round L-16 — does this post have a graphic/video attached? */
  has_attachment: boolean;
  attachment_mime: string | null;
  attachment_name: string | null;
}
export function getApprovalQueue(): Promise<{ items: ApprovalQueueItem[] }> {
  return apiFetch('/api/team/approval-queue');
}

// ---------------------------------------------------------------------
// Client activity feed — buyer/seller/lender re-engagement signals
// ---------------------------------------------------------------------

export interface ClientActivityItem {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  last_login_at: number | null;
  login_count: number;
  portal: 'client' | 'partner';
  last_view_at: number | null;
  views_last_7d: number | null;
  last_view_transaction_id: number | null;
  last_view_address_street: string | null;
  last_view_address_city: string | null;
  last_view_address_state: string | null;
}

export function getClientActivity(days = 14): Promise<{ items: ClientActivityItem[] }> {
  return apiFetch(`/api/team/client-activity?days=${days}`);
}

/**
 * Upload a team logo file (image, max 1MB). Saves to R2 and updates
 * teams.logo_url to the resulting public URL. Returns the new URL so the
 * caller can re-render the preview.
 */
export async function uploadTeamLogo(file: File): Promise<{ ok: true; logo_url: string }> {
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
  const { getToken } = await import('./tokenStore');
  const token = getToken();
  if (!token) throw new Error('Not signed in');

  const formData = new FormData();
  formData.set('file', file);

  // We hand-build the fetch instead of using apiFetch because apiFetch sets
  // Content-Type: application/json, which would break multipart upload.
  const res = await fetch(`${apiBase}/api/team/logo`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const text = await res.text();
  let body: { ok?: true; logo_url?: string; error?: string };
  try {
    body = JSON.parse(text);
  } catch {
    body = { error: text || `HTTP ${res.status}` };
  }
  if (!res.ok || !body.ok || !body.logo_url) {
    throw new Error(body.error ?? `Upload failed (HTTP ${res.status})`);
  }
  return { ok: true, logo_url: body.logo_url };
}

/**
 * Upload a brokerage compliance logo (e.g. Keller Williams Hometown Partners).
 * Mirrors uploadTeamLogo but writes to teams.brokerage_logo_url. The team
 * logo and the brokerage logo are both rendered side-by-side at the bottom
 * of the public listing page so MLS/NAR compliance is visible.
 */
export async function uploadBrokerageLogo(file: File): Promise<{ ok: true; brokerage_logo_url: string }> {
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
  const { getToken } = await import('./tokenStore');
  const token = getToken();
  if (!token) throw new Error('Not signed in');

  const formData = new FormData();
  formData.set('file', file);

  const res = await fetch(`${apiBase}/api/team/brokerage-logo`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const text = await res.text();
  let body: { ok?: true; brokerage_logo_url?: string; error?: string };
  try {
    body = JSON.parse(text);
  } catch {
    body = { error: text || `HTTP ${res.status}` };
  }
  if (!res.ok || !body.ok || !body.brokerage_logo_url) {
    throw new Error(body.error ?? `Upload failed (HTTP ${res.status})`);
  }
  return { ok: true, brokerage_logo_url: body.brokerage_logo_url };
}
